import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, FileText, Link as LinkIcon, Pencil, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import ResourceFormDialog, { type ResourceRow } from "./ResourceFormDialog";

export default function ResourcesTab({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ResourceRow | null>(null);

  const list = useQuery({
    queryKey: ["partner-resources", orgId],
    queryFn: async (): Promise<ResourceRow[]> => {
      const { data, error } = await supabase
        .from("partner_resources")
        .select("*")
        .eq("partner_org_id", orgId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ResourceRow[];
    },
  });

  const del = useMutation({
    mutationFn: async (row: ResourceRow) => {
      if (row.file_path) {
        await supabase.storage.from("partner-resources").remove([row.file_path]);
      }
      const { error } = await supabase.from("partner_resources").delete().eq("id", row.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Resource removed");
      qc.invalidateQueries({ queryKey: ["partner-resources", orgId] });
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to remove"),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Upload files or share links with the community. Published resources appear on your public partner profile.
        </p>
        <Button onClick={() => { setEditing(null); setOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" /> Add resource
        </Button>
      </div>

      {list.isLoading ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (list.data ?? []).length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          No resources yet. Add a link or upload a document to get started.
        </Card>
      ) : (
        <div className="grid gap-3">
          {list.data!.map((r) => (
            <Card key={r.id} className="p-4 flex items-start gap-4">
              <div className="mt-1 text-muted-foreground">
                {r.resource_type === "file" ? <FileText className="h-5 w-5" /> : <LinkIcon className="h-5 w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-medium text-sm truncate">{r.title}</h3>
                  {r.focus_area && <Badge variant="secondary" className="text-[10px]">{r.focus_area}</Badge>}
                  {r.published ? (
                    <Badge variant="outline" className="text-[10px]">Published</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] opacity-60">Draft</Badge>
                  )}
                </div>
                {r.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.description}</p>}
                {r.url && (
                  <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary inline-flex items-center gap-1 mt-1 hover:underline">
                    <ExternalLink className="h-3 w-3" /> {r.url}
                  </a>
                )}
                {r.file_path && (
                  <p className="text-[11px] text-muted-foreground mt-1">File: {r.file_path.split("/").pop()}</p>
                )}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => { setEditing(r); setOpen(true); }}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => {
                  if (confirm(`Delete "${r.title}"?`)) del.mutate(r);
                }}>
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <ResourceFormDialog
        open={open}
        onOpenChange={setOpen}
        orgId={orgId}
        editing={editing}
        onSaved={() => qc.invalidateQueries({ queryKey: ["partner-resources", orgId] })}
      />
    </div>
  );
}